
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, CreditCard, Package } from "lucide-react";
import { format } from "date-fns";
import { useProviderById } from "@/hooks/useProviders";
import type { UserMembership } from "@/hooks/useUserMemberships";

interface UserMembershipCardProps {
  membership: UserMembership & {
    isSubscription?: boolean;
    subscriptionStatus?: string;
  };
}

const UserMembershipCard = ({ membership }: UserMembershipCardProps) => {
  const { data: provider } = useProviderById(membership.membership.provider_id);

  const getStatusBadge = () => {
    // Handle subscription-based memberships
    if (membership.isSubscription) {
      if (membership.subscriptionStatus === 'active') {
        return <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>;
      } else if (membership.subscriptionStatus === 'canceled') {
        return <Badge variant="destructive">Canceled</Badge>;
      } else if (membership.subscriptionStatus === 'past_due') {
        return <Badge variant="secondary">Past Due</Badge>;
      } else if (membership.subscriptionStatus === 'incomplete') {
        return <Badge variant="secondary">Incomplete</Badge>;
      } else {
        return <Badge variant="secondary">Expired</Badge>;
      }
    }

    // Handle regular memberships
    if (!membership.is_active) {
      return <Badge variant="secondary">Inactive</Badge>;
    }
    
    // Check if membership has expired
    if (membership.expires_at) {
      const now = new Date();
      const expiresAt = new Date(membership.expires_at);
      if (now > expiresAt) {
        return <Badge variant="destructive">Expired</Badge>;
      }
    }
    
    // Check if sessions are remaining for pack_of_sessions
    if (membership.membership.membership_type === 'pack_of_sessions' && 
        membership.sessions_remaining === 0) {
      return <Badge variant="secondary">Used Up</Badge>;
    }
    
    return <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>;
  };

  const formatPurchaseDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  const getCreditsDisplay = () => {
    if (membership.isSubscription) {
      if (membership.membership.membership_type === 'sessions_per_month') {
        return `${membership.sessions_remaining || 0} of ${membership.membership.session_count || 0} this month`;
      }
      return 'Unlimited (Subscription)';
    }
    if (membership.membership.membership_type === 'unlimited') {
      return 'Unlimited';
    }
    if (membership.membership.membership_type === 'sessions_per_month') {
      return `${membership.sessions_remaining || 0} this month`;
    }
    return `${membership.sessions_remaining || 0} remaining`;
  };

  const getTypeDisplay = () => {
    if (membership.isSubscription) {
      if (membership.membership.membership_type === 'sessions_per_month') {
        return 'Monthly Sessions';
      }
      return 'Subscription';
    }
    switch (membership.membership.membership_type) {
      case 'pack_of_sessions':
        return 'Pack';
      case 'sessions_per_month':
        return 'Monthly';
      case 'unlimited':
        return 'Unlimited';
      default:
        return 'Package';
    }
  };

  const shouldShowSessionsProgress = () => {
    return membership.membership.membership_type === 'sessions_per_month' || 
           membership.membership.membership_type === 'pack_of_sessions';
  };

  const getSessionsProgress = () => {
    if (!shouldShowSessionsProgress()) return null;
    
    const total = membership.membership.session_count || 0;
    const remaining = membership.sessions_remaining || 0;
    const used = membership.sessions_used || 0;
    
    return { total, remaining, used };
  };

  const progress = getSessionsProgress();

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-semibold text-lg mb-1">{membership.membership.name}</h3>
            <p className="text-sm text-gray-600">{provider?.name || 'Loading provider...'}</p>
          </div>
          {getStatusBadge()}
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <CreditCard className="h-4 w-4" />
              <span>Credits:</span>
            </div>
            <span className="font-medium">{getCreditsDisplay()}</span>
          </div>

          {progress && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Sessions used</span>
                <span>{progress.used} of {progress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${progress.total > 0 ? (progress.used / progress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Package className="h-4 w-4" />
              <span>Type:</span>
            </div>
            <span className="font-medium">{getTypeDisplay()}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Calendar className="h-4 w-4" />
              <span>Started:</span>
            </div>
            <span className="font-medium">{formatPurchaseDate(membership.purchase_date)}</span>
          </div>
        </div>
        
        {membership.membership.description && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-600">{membership.membership.description}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UserMembershipCard;
